import { Box, Button, Grid, Stack, Typography } from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import get from "lodash/get";
import { useEffect, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link as RouterLink, useParams } from "react-router-dom";
import Breadcrumb from "../../components/Breadcrumb";
import MainLayout from "../../components/MainLayout";
import Maps from "../../components/Map";
import useAlert from "../../hooks/useAlert";
import useNotification from "../../hooks/useNotification";
import service from "../../services/property";
import roomService from "../../services/room";
import queryDefault from "../../utils/queryDefault";
import LoadingPage from "../LoadingPage";

const CustomNoRowOverlay = () => (
  <Box sx={{ pt: 5 }}>
    <Typography variant="body1" textAlign="center" fontWeight="600">
      Sorry, data is not found
    </Typography>
  </Box>
);

export default function PropertyDetail() {
  const { id } = useParams();
  const { data, isLoading, isFetched, refetch } = useQuery(
    "current-property",
    () => service.getPropertyById(Number(id))
  );

  const alert = useAlert();
  const notification = useNotification();

  const [pageSize, setPageSize] = useState<number>(10);
  const [options, setOptions] = useState<any>({});
  const [markerPosition, setMarkerPosition] = useState<google.maps.LatLngLiteral>({
    lat: -6.1753917,
    lng: 106.8271533,
  });

  useEffect(() => {
    if (isFetched) {
      setMarkerPosition({
        lat: get(data, "data.location.x"),
        lng: get(data, "data.location.y"),
      });
    }
  }, [isFetched, data]);

  const deleteRoom = useMutation(
    "delete-room",
    roomService.deleteRoom,
    {
      onSuccess: () => {
        refetch();
        notification.onOpen({
          message: "Room was successfully deleted!",
          type: "success",
          position: "top",
        });
      },
      onError: (error: any) => {
        refetch();
        notification.onOpen({
          message:
            error?.response?.data?.message === undefined
              ? "Something went wrong"
              : error?.response?.data?.message,
          type: "error",
          position: "top",
        });
      },
      ...queryDefault,
    }
  );
  const onDelete = (id: number) => {
    alert.onClose();
    deleteRoom.mutate(id);
  };

  const onClickDelete = (id: number) => {
    alert.onOpen({
      title: "Confirm Delete?",
      message: "Are you sure want to delete this room?",
      actions: [
        {
          color: "error",
          variant: "contained",
          children: "Delete",
          onClick: () => onDelete(id),
        },
        {
          color: "primary",
          variant: "outlined",
          children: "Cancel",
          onClick: () => alert.onClose(),
        },
      ],
    });
  };

  return isLoading ? (
    <LoadingPage />
  ) : (
    <MainLayout>
      <Breadcrumb
        label={get(data, "data.property.name") || ""}
        breadcrumbs={[
          { label: "Properties", href: `/app/properties/property` },
          { label: `Property ${get(data, "data.property.name")}`, href: `/app/properties/property/${id}` },
        ]}
      />

      <Grid container spacing={2}>
        <Grid item lg={4} xs={12}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Name
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.name}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Description
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.description}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Is Favorite
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data.data.property.is_favorite ? "Yes" : "No"}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Cluster
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.cluster_name}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Owner
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.user_name}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Place
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {`${data?.data?.property?.sub_district_name}, ${data?.data?.property?.district_name}, ${data?.data?.property?.city_name}, ${data?.data?.property?.state_name}`}
            </Typography>
          </Stack>
        </Grid>

        <Grid item lg={4} md={12}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Facilities
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.facilities.map((item: any) => item.name).join(", ")}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Type
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.type}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Car Parking
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.park_car}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Motorcycle Parking
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.park_motorcycle}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Address
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.address && data?.data?.property?.address !== "" ? data.data.property.address : "-"}
            </Typography>
          </Stack>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Deposit
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              {data?.data?.property?.deposit}
            </Typography>
          </Stack>
        </Grid>

        <Grid item lg={4} md={12}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="body1" color="grey.600" fontWeight="300">
              Location
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              Latitude : {data.data.property.location.x}
            </Typography>
            <Typography
              variant="body1"
              color="green"
              fontWeight="700"
              sx={{ mt: 0.3 }}
            >
              Longitude : {data.data.property.location.y}
            </Typography>
          </Stack>
          <Box
            sx={{
              height: "300px",
              width: "300px",
            }}
          >
            <Maps center={markerPosition} />
          </Box>
        </Grid>

        <Grid item lg={12} md={12} mt={3}>
          <Stack sx={{ mt: 2 }}>
            <Typography variant="h3">Photos</Typography>
            {data?.data?.photos.map((photo: any) => (
              <Box mt={2}>
                <img
                  src={process.env.REACT_APP_BACKEND_URL + photo.photo}
                  alt="Property"
                  style={{
                    width: "100%",
                    borderRadius: "0.375rem",
                    objectFit: "cover",
                    alignSelf: "start"
                  }}
                />
              </Box>
            ))}
          </Stack>
        </Grid>
      </Grid>

      <Typography variant="h2" mt={3}>
        Room List
      </Typography>

      <Box sx={{ mt: 2 }}>
        <Stack direction="row" spacing={1}>
          <Button
            variant="contained"
            component={RouterLink}
            to={`/app/properties/property/${id}/room/create`}
          >
            ADD ROOM
          </Button>
        </Stack>
      </Box>

      <Box mt={2}>
        <DataGrid
          autoHeight
          loading={isLoading}
          getRowId={(record) => get(record, "id")}
          rows={get(data, "data.rooms", [])}
          rowCount={get(data, "data.total_room", 0)}
          page={options.page_number}
          pageSize={pageSize}
          rowsPerPageOptions={[10, 20, 30]}
          pagination
          disableSelectionOnClick
          onPageChange={(newPage) => {
            setOptions((prev: any) => ({ ...prev, page_number: newPage }));
          }}
          onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
          components={{
            NoRowsOverlay: CustomNoRowOverlay,
          }}
          columns={[
            {
              field: "name",
              headerName: "Name",
              flex: 1,
              filterable: false,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography
                      sx={{ textDecoration: "none" }}
                      component={RouterLink}
                      to={`/app/properties/property/room/detail/${get(
                        params,
                        "row.id"
                      )}`}
                    >{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "description",
              headerName: "Description",
              flex: 1,
              filterable: false,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "facilities",
              headerName: "Facilities",
              flex: 1,
              filterable: false,
              renderCell: (params) => {
                return (
                  <Stack>
                    <Typography>{params.value}</Typography>
                  </Stack>
                );
              },
            },
            {
              field: "actions",
              headerName: "Action",
              sortable: false,
              filterable: false,
              width: 200,
              renderCell: (params) => {
                return (
                  <Stack direction="row" spacing={1}>
                    <Button
                      variant="outlined"
                      color="primary"
                      component={RouterLink}
                      to={`/app/properties/property/room/edit/${get(
                        params,
                        "row.id"
                      )}`}
                    >
                      Edit
                    </Button>

                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => onClickDelete(params.row.id)}
                    >
                      Delete
                    </Button>
                  </Stack>
                );
              },
            },
          ]}
          sx={{
            ".MuiDataGrid-columnHeaderTitle": {
              fontWeight: 700,
            },
          }}
        />
      </Box>
    </MainLayout>
  );
}
